<template>
    <div class="mt-5">
        <dispute-filter-modal :owner-options="owners"></dispute-filter-modal>
        <dispute-response-modal></dispute-response-modal>
        <dispute-association-modal></dispute-association-modal>
        <div class="border-bottom d-flex justify-content-between align-items-end">
            <div class="h4">
                <a href="javascript:void(0)" class="mr-2" role="button" @click="collapsed = !collapsed">
                    <i :class="['fas text-muted', collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                </a>
                Medical Treatment Disputes
                <span class="font-small-italic text-muted">(LWC Form 1009)</span>          
            </div>                
            <div v-if="!collapsed" class="btn-toolbar" role="toolbar">
                <div class="input-group input-group-sm mr-2">
                    <div class="input-group-prepend">
                        <div class="input-group-text"><i class="fas fa-search fa-sm text-muted"></i></div>
                    </div>
                    <input type="text" class="form-control" placeholder="Search Descriptions" v-model="searchString">
                </div>
                <div class="btn-group"> 
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Filter" @click="showFilters()">
                        <i class="fas fa-filter fa-lg"></i>
                    </button>
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Upload File" @click="uploadDocument()">
                        <i class="fas fa-file-upload fa-lg"></i>
                    </button>
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="New Dispute" @click="generate1009()">
                        <i class="fas fa-gavel fa-lg"></i>
                    </button>
                </div>
            </div>
        </div>

        <div id="disputesTable" :class="['collapse', { 'show': !collapsed }]">
            <div v-if="isLoading" class="pt-3">            
                <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
            </div>
            <div v-else-if="filteredDocuments.length === 0" class="border-top"><h3 class="text-muted text-center mt-3">No Documents.</h3></div>
            <div v-else>
                <div v-if="selectedDocument">
                    <div v-if="transmissionsDisabled" class="alert alert-danger fade show px-3 py-2 mt-2 font-weight-bold">
                        Transmitting documents has been disabled.
                    </div>                    
                    <div v-else class="alert alert-primary fade show px-3 py-2 mt-2 d-flex justify-content-between">
                        <div>                    
                            <submit-button @clickfax="showFaxSubmission()" @clickemail="showEmailSubmission()" :emailEnabled="emailEnabled"></submit-button>                     
                            <button type="button" class="btn btn-sm btn-secondary ml-3 font-weight-bold" @click="clearSelections()">CANCEL</button>                    
                        </div>
                    </div>
                </div>
                <div v-else class="m-2">
                    <span class="fa-stack fa-xs instructions-icon">
                        <i class="far fa-circle fa-stack-2x"></i>
                        <i class="fas fa-info fa-stack-1x fa-sm"></i>
                    </span>
                    <span class="instructions">Use the radio buttons to initiate submitting a Dispute document to OWCA - Medical Services.</span>
                </div>                    
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th></th>
                            <th class="fit">Document Type</th>
                            <th>Description</th>
                            <th class="fit">Owner</th>
                            <th class="fit">Uploaded/Received</th>
                            <th class="fit">Submitted</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="doc in filteredDocuments">
                            <tr class="table-stripe" :key="'dispute-document-' + doc.id">
                                <td class="fit">
                                    <div class="d-flex">
                                        <div class="custom-control custom-radio">
                                            <input :id="'dispute-document-radio-' + doc.id" type="radio" class="custom-control-input" :value="doc" v-model="selectedDocument">
                                            <label class="custom-control-label align-top" :for="'dispute-document-radio-' + doc.id">
                                                <span class="sr-only"></span>
                                            </label>
                                        </div>                                     
                                        <div>
                                            <a v-if="doc.associated_documents.length" href="javascript:void(0)" data-toggle="collapse" role="button" @click="doc.collapsed = !doc.collapsed">
                                                <i :class="['fas fa-lg text-muted', doc.collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                                            </a>
                                        </div>
                                    </div>                                                 
                                </td>
                                <td class="fit pr-3">                                
                                    <document-status-icon :document-id="doc.id" :document-type-id="doc.document_type_id" :document-status="doc.document_status"></document-status-icon>                             
                                    <i :class="['text-muted mr-1', getIconClass(doc)]"></i>
                                    <a :href="'/claims/' + claimId + '/documents/' + doc.id" target="_blank">{{ doc.document_type.name }}</a>                                                                               
                                </td>
                                <td>
                                    <input v-if="doc.edit" class="form-control form-control-sm" v-model="doc.description" @blur="changeDescription(doc)" @keyup.enter="changeDescription(doc)" v-focus>
                                    <span v-else @click="doc.edit = true;" :title="doc.description">                                
                                        {{ doc.description | truncate(80) }}
                                        <a href="javascript:void(0)" class="edit-button"><i class="fas fa-pencil-alt fa-xs"></i></a>
                                    </span>
                                </td>
                                <td class="fit pr-3">{{ doc.owner }}</td>
                                <td class="fit pr-3">{{ doc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                <td class="fit pr-3">{{ doc.submitted_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                <td class="fit text-right">
                                    <div class="d-flex flex-nowrap flex-row justify-content-end">

                                        <!-- show submission history -->
                                        <div class="text-center">
                                            <button v-if="doc.can_show_history" class="btn btn-link px-1" title="Submission History" @click="showSubmissionHistory(doc.id)">
                                                <span class="fa-stack fa-xs fa-fw"> 
                                                    <i class="fas fa-history fa-lg fa-stack-2x"></i>
                                                    <i :class="['fas fa-lg fa-stack-1x history-badge', 
                                                        { 'fa-check-circle text-success': doc.last_submission_status_code == TransmissionStatus.SUCCESS, 
                                                          'fa-exclamation-circle text-danger': doc.last_submission_status_code == TransmissionStatus.FAILURE,
                                                          'fa-spinner fa-spin text-secondary': doc.last_submission_status_code == TransmissionStatus.IN_PROGRESS }]"></i>
                                                </span>
                                            </button>
                                            <button v-else class="btn btn-link px-1">
                                                <span class="fa-stack fa-xs fa-fw icon-hidden"> 
                                                    <i class="fas fa-history fa-lg fa-stack-2x"></i>
                                                    <i class="fas fa-lg fa-stack-1x history-badge fa-circle"></i>
                                                </span>
                                            </button>                                            
                                        </div>

                                        <!-- associated response with document -->
                                        <div class="text-center">
                                            <button v-if="!doc.submitted" class="btn btn-link px-1 px-1" title="Link with Form 1010(s)" @click="associate(doc)">
                                                <i class="fas fa-link fa-lg fa-fw"></i>
                                            </button>
                                            <button v-else class="btn btn-link px-1 px-1">
                                                <i class="fas fa-link fa-lg fa-fw"></i>
                                            </button>                                                                                    
                                        </div>                                        

                                        <!-- upload associated document -->
                                        <div class="text-center">
                                            <button class="btn btn-link px-1" title="Upload Associated Document" @click="uploadDocument(doc)">
                                                <i class="fas fa-file-upload fa-lg fa-fw"></i>
                                            </button>
                                        </div>                                                                         

                                        <!-- delete document -->
                                        <div class="flex-fill text-center">
                                            <button v-if="doc.can_delete" class="btn btn-link px-2" title="Delete Document" @click="confirmDelete(doc.id)">
                                                <i class="fas fa-trash-alt fa-lg fa-fw"></i>
                                            </button>
                                            <button v-else class="btn btn-link px-1">
                                                <i class="fas fa-trash-alt fa-lg fa-fw icon-hidden"></i>
                                            </button>                                                                             
                                        </div>                                                              
                                    </div>
                                </td>
                            </tr>

                            <template id="associatedDocuments" class="collapse">
                                <tr v-show="!doc.collapsed" v-for="associatedDoc in doc.associated_documents" :key="'auth-associated-' + doc.id + '-' + associatedDoc.id" class="font-small">
                                    <td class="fit">
                                        <div class="d-flex">
                                            <div><i class="fas fa-lg fa-fw"></i></div>
                                        </div>
                                    </td>
                                    <td class="fit pl-4">
                                        <span :class="associatedDoc.read_by ? 'cursor-pointer' : ''" data-toggle="popover" data-placement="left" :data-content="getReadContent(associatedDoc)">
                                            <i :class="['text-muted mr-1', getIconClass(associatedDoc)]"></i>
                                        </span>
                                        <a :href="'/claims/' + claimId + '/documents/' + associatedDoc.id" target="_blank" :class="getReadClass(associatedDoc)">{{ associatedDoc.document_type.name }}</a>                             
                                    </td>
                                    <td>
                                        <input v-if="associatedDoc.edit" class="form-control form-control-sm" v-model="associatedDoc.description" @blur="changeDescription(associatedDoc)" @keyup.enter="changeDescription(associatedDoc)" v-focus>
                                        <span v-else @click="associatedDoc.edit = true;" :title="associatedDoc.description">                                    
                                            {{ associatedDoc.description | truncate(80) }}
                                            <a href="javascript:void(0)" class="edit-button"><i class="fas fa-pencil-alt fa-xs"></i></a>
                                        </span>
                                    </td>
                                    <td class="fit">{{ associatedDoc.owner }}</td>
                                    <td class="fit">{{ associatedDoc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                    <td class="fit">{{ associatedDoc.submitted_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                    <td class="fit">
                                        <div class="d-flex flex-nowrap flex-row justify-content-end">

                                            <div class="text-center">
                                                <!-- disassociate document -->
                                                <button  v-if="associatedDoc.can_disassociate" class="btn btn-link px-1" title="Unlink from Document" @click="confirmDisassociate(doc.id, associatedDoc.id)">
                                                    <i class="fas fa-unlink fa-lg fa-fw"></i>
                                                </button>

                                                <!-- delete document -->
                                                <button v-else-if="associatedDoc.can_delete" class="btn btn-link px-1" title="Delete Document" @click="confirmDelete(associatedDoc.id)">
                                                    <i class="fas fa-trash-alt fa-lg fa-fw"></i>
                                                </button>

                                                <!-- non-clickable button to keep spacing -->
                                                <button v-else class="btn btn-link px-1">
                                                    <i class="fas fa-trash-alt fa-lg fa-fw icon-hidden"></i>
                                                </button>                                                                                                                                                 
                                            </div>

                                        </div>
                                    </td>                        
                                </tr>
                            </template>                            

                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { DocumentType, TransmissionStatus } from '../../constants.js';
import DocumentStatusIcon from './DocumentStatusIconComponent.vue';
import DisputeFilterModal from './DisputeFilterComponent.vue';
import DisputeResponseModal from './DisputeResponseComponent.vue';
import DisputeAssociationModal from './DisputeAssociationComponent.vue';
import SubmitButton from '../buttons/SubmitButtonComponent.vue';

export default {
     components: {
        DocumentStatusIcon,
        DisputeFilterModal,
        DisputeResponseModal,
        DisputeAssociationModal,
        SubmitButton
    },
    props: {
        claimId: Number,
        emailEnabled: Boolean,
    },
    data() {
        return {

            // import constants into the template
            DocumentType: DocumentType,
            TransmissionStatus: TransmissionStatus,

            documents: [],
            selectedDocument: null,
            searchString: '',
            filters: {
                status: '',
                owner: '',
                submitted: {
                    begin: null,
                    end: null
                }
            },
            collapsed: false,
            isLoading: false,
            options: [],
            transmissionsDisabled: false
        }        
    },
    computed: {
        owners: function () {
            return [...new Set(this.documents.map(e => e.owner))];
        },        
        filteredDocuments: function () {
            const self = this;
            let retval = this.documents;

            // filter on description
            retval = retval.filter(doc => (doc.description.toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1));            

            if (self.filters) {             

                // filter on status
                if (self.filters.status !== '') {
                    retval = retval.filter(e => e.document_status.id == self.filters.status);
                }

                // filter on submitted date; we have to convert the date to local time and drop the time
                // portion in order to do the comparison properly
                if (self.filters.submitted.begin && self.filters.submitted.end) {
                    retval = retval.filter(e => {
                        let submittedAt = self.getLocalDate(e.submitted_at);
                        return (submittedAt >= self.filters.submitted.begin && submittedAt <= self.filters.submitted.end) ||
                            e.associated_documents.filter(x => {
                                let submittedAt = self.getLocalDate(x.submitted_at);
                                return submittedAt >= self.filters.submitted.begin && submittedAt <= self.filters.submitted.end
                            }).length;
                    });
                }
            }
    
            return retval;
        }
    },
    methods: {
        refresh: function (reset=false) {
            
            // only display loading indicator when page is initially loading
            this.isLoading = reset;
            this.selectedDocument = null;
            axios.get('/ajax/claims/' + this.claimId + '/disputes')
                .then(response => {

                    this.transmissionsDisabled = response.data.transmissions_disabled;

                    const inboundTypes = [
                        DocumentType.INBOUND_FAX, 
                        DocumentType.LWC_FORM_1009_RESPONSE
                    ];

                    if (reset) {
                        this.collapsed = response.data.collapsed;
                        this.options = response.data.options;
                    }

                    // retain the previous collapsed states
                    let collapseStates = this.documents.map(d => { return {id: d.id, collapsed: d.collapsed}; });                    

                    // set the edit flag and collapsed flag for each document 
                    this.documents = response.data.documents.map(e => {

                        // add selected flag to each document
                        this.$set(e, 'selected', false);

                        // add edit flag to each document and associated documents
                        this.$set(e, 'edit', false);
                        e.associated_documents = e.associated_documents.map(x => {
                            this.$set(x, 'edit', false);
                            return x;
                        });                        

                        if (reset) {

                            // only collapse if all associated documents have been read
                            let unreadDocs = e.associated_documents.filter(a => { 
                                return inboundTypes.includes(a.document_type_id) && !a.read_by; 
                            }); 

                            this.$set(e, 'collapsed', unreadDocs.length === 0);
                        }
                        else {

                            let doc = collapseStates.find(b => b.id === e.id);                                                      
                            this.$set(e, 'collapsed', doc ? doc.collapsed : false);
                        }

                        return e; 
                    });

                    // enable popover tooltips
                    this.$nextTick(() => {
                        $('[data-toggle="tooltip"]').tooltip();
                        $('[data-toggle="popover"]').popover();                        
                    });

                    this.isLoading = false;                  
                })
                .catch(error => {
                    this.isLoading = false;
                });
        },
        getIconClass: function (doc) {
            let icon = 'fas fa-file';
            switch(parseInt(doc.document_type_id)) {
                case DocumentType.LWC_FORM_1010:
                    icon = 'far fa-file-pdf';
                    break;
                case DocumentType.INBOUND_FAX:
                case DocumentType.LWC_FORM_1010A:
                case DocumentType.LWC_FORM_1010_RESPONSE:
                case DocumentType.LWC_FORM_1009_RESPONSE:
                    icon = doc.read_at ? 'far fa-envelope-open' : 'far fa-envelope';
                    break;
                case DocumentType.LWC_FORM_1009:
                    icon = 'fas fa-file-contract';
                    break;
                case DocumentType.LWC_FORM_1010A_RESPONSE:
                    icon = 'fas fa-file-alt';
                    break;                    
                default:
                    break;
            }
            return icon + ' fa-lg';
        },
        getReadClass: function (doc) {
            const docTypes = [
                DocumentType.INBOUND_FAX,
                DocumentType.LWC_FORM_1010_RESPONSE,
                DocumentType.LWC_FORM_1009_RESPONSE,
                DocumentType.LWC_FORM_1010A
            ];

            return (docTypes.includes(doc.document_type_id) && !doc.read_at) ? 'font-weight-bold' : '';
        },
        getReadContent: function (doc) {
            return doc.read_by ? 'Read by ' +  doc.read_by.full_name + ' on ' +  this.$options.filters.localizeTime(doc.read_at, 'YYYY-MM-DD HH:mm:ss') : '';
        },
        changeDescription: function (document) {

            document.edit = false;

            // update the model
            this.$emit('update');

            // send the change to the server
            axios.patch('/ajax/documents/' + document.id + '/description', {
                'description': document.description,
            })
            .then(response => {
                // nothing to do
            })
            .catch(error => {
                // nothing to do
            });          
        },        
        showFaxSubmission: function () {
            let documents = [{
                    id: this.selectedDocument.id,
                    document_type: {
                        id: this.selectedDocument.document_type_id,
                        name: this.selectedDocument.document_type.name
                    },
                    description: this.selectedDocument.description
                }];
            eventHub.$emit('show-fax-submission', DocumentType.LWC_FORM_1009, documents);
        },
        showEmailSubmission: function () {
            let documents = [{
                    id: this.selectedDocument.id,
                    document_type: {
                        id: this.selectedDocument.document_type_id,
                        name: this.selectedDocument.document_type.name
                    },
                    description: this.selectedDocument.description
                }];            
            eventHub.$emit('show-email-submission', DocumentType.LWC_FORM_1009, documents);
        },
        showSubmissionHistory: function (id) {
            eventHub.$emit('show-submission-history', id);
        },
        generate1009: function() {
            eventHub.$emit('show-form-1009', this.claimId);
        },       
        confirmDelete: function (id) {
            eventHub.$emit('show-delete-document-confirmation', id);
        },
        showFilters: function () {
            eventHub.$emit('show-dispute-filters', this.filters, this.filter);
        },
        filter: function (filters) {
            this.filters = filters;
        },
        getLocalDate: function (value) {

            let retval = null;

            if (value) {
                let date = new Date(value);
                retval = new Date(date.getTime() - (date.getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0];
            }

            return retval;
        },
        uploadDocument: function (doc) {

            // default to uploading a form 1009
            let selectedOption = DocumentType.LWC_FORM_1009;
            let options = this.options.map(x => {
                return {
                    id: x.id,
                    name: x.name
                };
            });

            // a document will be passed in if we are uploading an associated document
            if (doc) {

                // set the default selected document type and selection options
                selectedOption = DocumentType.LWC_FORM_1009_RESPONSE;
                options = this.options.find(x => x.id == DocumentType.LWC_FORM_1009).associated_document_types;
            }

            // show the upload file modal
            eventHub.$emit('show-file-upload', {
                claimId: this.claimId,
                url: '/ajax/claims/' + this.claimId + '/upload',
                options: options,
                documentType: selectedOption,
                associateToDocumentId: doc ? doc.id : null
            });                      
        },        
        markRead: function (document) {

            if (document) {

                // we need to update every place the document appears as an associated document
                this.documents.forEach(d => {
                    let associatedDoc = d.associated_documents.find(e => e.id == document.id);
                    if (associatedDoc) {
                        associatedDoc.read_at = document.read_at;
                        associatedDoc.read_by = document.read_by;
                    }                
                });
            }   
        },
        clearSelections: function () {
            this.selectedDocument = null;
        },
        associate: function (document) {
            eventHub.$emit('show-dispute-association', document);
        },
        confirmDisassociate: function (ancestorId, documentId) {
            const url = '/ajax/documents/' + documentId + '/disassociate';
            const message = 'Are you sure you want to unlink these documents?';
            eventHub.$emit('show-disassociate-confirmation', url, ancestorId, message);
        }
    },
    created() {
        this.refresh(true);
        eventHub.$on('update-claim-documents', this.refresh);
        eventHub.$on('update-document-read', this.markRead);
    },
    beforeDestroy() {
        eventHub.$off('update-claim-documents');
        eventHub.$off('update-document-read');
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }
}
</script>

<style scoped>
    .table-sm td {
        padding-top: 0;
        padding-bottom: 0;
        vertical-align: middle;
    }

    .table-stripe  {
        background-color:rgba(0,0,0,.05);
    }
       
    .edit-button {
        position: relative;
        top: -4px;
        opacity: 0;
    }

    .edit-button:hover {
        opacity: 1;
    }

    .btn-link {
        text-decoration: none;
    }

    .secure-email {
        position: relative;
        top: 4px;
        margin-right: 4px;
    }

    .lock {
        position: relative;
        top: 8px;
        left: 8px;
        color: #bcbcbc;
        font-size: 0.95em;
    }

    .shrink {
        position: relative;
        top: -1px;
        font-size: .685em;
    }

    .stack-icon {
        position: relative;
        top: 2px;
        font-size: 1.0em;
    }
    
    .history-badge {
        font-size: 1em;
        position: relative;
        top: -12px;
        left: 10px;
    }     

    .instructions-icon {
        font-size: .60em;
        font-weight: bold;
        color: #6c757d;
    }

    .instructions {
        font-size: .90em;
        font-style: italic;
        font-weight: bold;
        color: #6c757d;
        position: relative;
        top: 2px;
    }
    
    /* overriding bootstrap unchecked background color; everything else remains the same */
    .custom-control-label::before {
        background-color: #ffffff;
        border: 1px solid #bbb;
          border-radius: 0.25rem;
    }

    .font-small {
        font-size: .95em;
    }

    .font-small-italic {
        font-size: .60em;
        font-style: italic;
    }

    .icon-hidden {
       color: transparent; 
    }
    
    .fa-unlink {
        font-size: .90em;
    }

    .input-group {
        align-self: flex-start;
    }          
</style>